import { Drawer } from '@mantine/core';
import clsx from 'clsx';
import { isEmpty, mapValues, orderBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useMeasure } from 'react-use';
import { darkModeRoot } from 'src/app/App.css';
import { FilterToolbarItemId } from 'src/components/Filters';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useMobileDrawerContext } from 'src/contexts/MobileDrawerContext';
import {
  ListingTileFilters,
  ListingTileFilterTarget,
  ListingTileQuantityFilter,
  useMobileInventorySortAndFilterContext,
} from 'src/contexts/MobileSortAndFilterContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Button, Stack } from 'src/core/ui';
import { useComputedState } from 'src/hooks/useComputedState';
import { ChevronRightIcon } from 'src/svgs/Viagogo';
import { ListingWithComp } from 'src/tiles/ListingTilesV2';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';

import { InventoryEventTabs } from '../../../InventoryEventTabs';
import { ActionToAllFilters } from './components/ActionToAllFilters';
import { EventSelectedFilters } from './components/InventorySelectedFilters';
import { InventoryCustomFilter } from './InventoryCustomFilterContent';
import * as styles from './InventoryListingSortingContent.css';
import { ListingQuantityFilteringContent } from './ListingQuantityFilterContent';
import { ListingSectionFilterContent } from './ListingSectionFilterContent';

const OutstandingFilterItemIds: FilterToolbarItemId[] = [
  'hasUnsoldTickets',
  'isPricedAboveZero',
  'isSeatSaver',
];

export const InventoryListingFilterDrawer = ({
  show,
  allInventory,
  activeTab,
  onClose,
}: {
  show: boolean;
  activeTab: InventoryEventTabs;
  allInventory: ListingWithComp[];
  onClose: () => void;
}) => {
  const { isDarkMode } = useSiteTheme();
  const [headerRef, { height }] = useMeasure<HTMLElement>();

  const {
    onSaveFilterQuery,
    onResetFilters,
    tempQuery,
    selectedFilters,
    filters,
    canSaveFilter,
    listingTileFilters,
    setListingTileFilters,
    quickFilters,
    onResetCustomFilter,
    onSaveSelectedFilters,
    onFilterRemoved,
  } = useMobileInventorySortAndFilterContext();

  const [localListingTileFilters, setLocalListingTileFilters] =
    useComputedState<ListingTileFilters | undefined>(listingTileFilters);

  const hasNonOutstandingFilters =
    useMemo(() => {
      const nonOutstandingFilters = mapValues(selectedFilters, (groupFilters) =>
        groupFilters.filter(
          (filter) => !OutstandingFilterItemIds.includes(filter.filterId)
        )
      );
      return Object.values(nonOutstandingFilters).flatMap((filters) => filters);
    }, [selectedFilters]).length > 0;

  const selectedFilterCount = useMemo(
    () => Object.values(selectedFilters).flatMap((filters) => filters).length,
    [selectedFilters]
  );

  const onClearFilters = useCallback(() => {
    setListingTileFilters({
      listingQuantities: {
        quantities: [],
        target: ListingTileFilterTarget.All,
      },
      sections: [],
    });
    onResetFilters();
  }, [onResetFilters, setListingTileFilters]);

  const onAppFilters = useCallback(() => {
    setListingTileFilters(localListingTileFilters);
    onSaveFilterQuery();
    onClose();
  }, [
    localListingTileFilters,
    onClose,
    onSaveFilterQuery,
    setListingTileFilters,
  ]);

  const canSave = useMemo(() => {
    if (canSaveFilter) {
      return true;
    }
    return (
      !isEmpty(localListingTileFilters?.listingQuantities?.quantities) ||
      !isEmpty(localListingTileFilters?.sections)
    );
  }, [
    canSaveFilter,
    localListingTileFilters?.listingQuantities?.quantities,
    localListingTileFilters?.sections,
  ]);

  return (
    <Drawer.Root
      zIndex={50}
      opened={show}
      onClose={onClose}
      size="100%"
      position="right"
      className={clsx(globalMobileWrapper, {
        [darkModeRoot]: isDarkMode,
      })}
    >
      <Drawer.Overlay />
      <Drawer.Content
        bg={vars.color.backgroundSecondary}
        style={{ overflowY: 'hidden', color: vars.color.textPrimary }}
        className={styles.drawerContent}
      >
        <Drawer.Header
          ref={headerRef}
          bg={vars.color.backgroundSecondary}
          className={styles.drawerHeader}
        >
          <MobileHeaderBar
            title={
              hasNonOutstandingFilters ? (
                <FormatContent
                  id={FormatContentId.NFiltersSelected}
                  params={selectedFilterCount + ''}
                />
              ) : (
                <Content id={ContentId.FilterEventsBy} />
              )
            }
            actions={
              !hasNonOutstandingFilters && (
                <InventoryCustomFilter
                  quickFilters={quickFilters}
                  onResetCustomFilter={onResetCustomFilter}
                />
              )
            }
            onBack={onClose}
          />
        </Drawer.Header>
        <Drawer.Body
          bg={vars.color.backgroundSecondary}
          style={{ height: `calc(100% - ${height + 20}px`, overflow: 'hidden' }}
          className={styles.drawerBody}
        >
          <Stack
            height="full"
            direction="column"
            gap="m"
            className={styles.drawerInnerContent}
          >
            <Stack
              direction="column"
              gap="l"
              style={{ flex: 1, overflowY: 'auto' }}
            >
              {hasNonOutstandingFilters ? (
                <>
                  <EventSelectedFilters
                    tempQuery={tempQuery}
                    selectedDropdownItems={selectedFilters}
                    filters={filters}
                    onSaveSelectedFilters={onSaveSelectedFilters}
                    onFilterRemoved={onFilterRemoved}
                  />
                  <CustomListingTileFilter
                    inventory={allInventory}
                    activeTab={activeTab}
                    listingTileFilters={localListingTileFilters}
                    setListingTileFilters={setLocalListingTileFilters}
                  />
                </>
              ) : (
                <>
                  <ListingOutstandingFilters />
                  <CustomListingTileFilter
                    inventory={allInventory}
                    activeTab={activeTab}
                    listingTileFilters={localListingTileFilters}
                    setListingTileFilters={setLocalListingTileFilters}
                  />
                  <ActionToAllFilters
                    selectedFilters={selectedFilters}
                    filters={filters}
                    onSaveSelectedFilters={onSaveSelectedFilters}
                  />
                </>
              )}
            </Stack>
            <Stack
              justifyContent="spaceBetween"
              width="full"
              alignItems="center"
            >
              <Button
                style={{ height: '40px' }}
                shape="rect"
                variant="outlineGray"
                onClick={onClearFilters}
              >
                <Content id={ContentId.Clear} />
              </Button>
              <Button
                style={{ height: '40px' }}
                shape="rect"
                disabled={!canSave}
                onClick={onAppFilters}
              >
                <Content id={ContentId.Apply} />
              </Button>
            </Stack>
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};

type OutstandingFilterItem = {
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  isSelected: boolean;
  onToggleSelect: () => void;
};

type ListingOutstandingFilterKey =
  | 'hasUnsoldTickets'
  | 'isPricedAboveZero'
  | 'isSeatSaver';

const ListingOutstandingFilters = () => {
  const { tempQuery, setTempQuery } = useMobileInventorySortAndFilterContext();

  const onToggleFilter = useCallback(
    (key: ListingOutstandingFilterKey) => () => {
      const enabled = !tempQuery[key];
      setTempQuery({
        ...tempQuery,
        [key]: enabled,
      });
    },
    [setTempQuery, tempQuery]
  );

  const outstandingFilters = useMemo(() => {
    const filters: OutstandingFilterItem[] = [];
    filters.push({
      id: 'hasUnsoldTickets',
      title: <Content id={ContentId.HasUnsoldListings} />,
      isSelected: Boolean(tempQuery.hasUnsoldTickets),
      onToggleSelect: onToggleFilter('hasUnsoldTickets'),
    });
    filters.push({
      id: 'isPricedAboveZero',
      title: <Content id={ContentId.HasUnpricedListings} />,
      isSelected: Boolean(tempQuery.isPricedAboveZero),
      onToggleSelect: onToggleFilter('isPricedAboveZero'),
    });
    filters.push({
      id: 'isSeatSaver',
      title: <Content id={ContentId.Placeholder} />,
      isSelected: Boolean(tempQuery.isSeatSaver),
      onToggleSelect: onToggleFilter('isSeatSaver'),
    });
    return filters;
  }, [
    onToggleFilter,
    tempQuery.hasUnsoldTickets,
    tempQuery.isPricedAboveZero,
    tempQuery.isSeatSaver,
  ]);

  return (
    <Stack direction="column" gap="m">
      <span className={styles.subtitleAfter}>
        <Content id={ContentId.FilterBy} />
      </span>
      <Stack direction="column" gap="s">
        {outstandingFilters.map((filter) => {
          return (
            <Stack
              role="button"
              key={filter.id}
              className={styles.inventoryFilterGroupItem}
              alignItems="center"
              justifyContent="spaceBetween"
            >
              <Checkbox
                checked={filter.isSelected}
                label={filter.title}
                color={vars.color.textBrand}
                onChange={filter.onToggleSelect}
                labelPosition="right"
                checkboxSizeVariant="light"
              />
              {filter.description && (
                <span className={styles.sotringOrderDescription}>
                  {filter.description}
                </span>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

const CustomListingTileFilter = ({
  inventory,
  activeTab,
  listingTileFilters,
  setListingTileFilters,
}: {
  inventory: ListingWithComp[];
  activeTab: InventoryEventTabs;
  listingTileFilters?: ListingTileFilters;
  setListingTileFilters: (_?: ListingTileFilters) => void;
}) => {
  const { openWithContent, close } = useMobileDrawerContext();

  const { venueMapInfo } = useEventMapContext();

  const onUpdateSectionFilter = useCallback(
    (sections?: string[]) => {
      setListingTileFilters({
        ...listingTileFilters,
        sections,
      });
    },
    [listingTileFilters, setListingTileFilters]
  );

  const onUpdateQuantityFilter = useCallback(
    (quantityFilter?: ListingTileQuantityFilter) => {
      setListingTileFilters({
        ...listingTileFilters,
        listingQuantities: quantityFilter,
      });
    },
    [listingTileFilters, setListingTileFilters]
  );

  const { sectionsSubtitle, quantitiesSubtitle } = useMemo(() => {
    const sections = listingTileFilters?.sections ?? [];
    const quantities = listingTileFilters?.listingQuantities?.quantities ?? [];
    return {
      sectionsSubtitle: sections.join(', '),
      quantitiesSubtitle: orderBy(quantities, (q) => parseInt(q)).join(', '),
    };
  }, [
    listingTileFilters?.listingQuantities?.quantities,
    listingTileFilters?.sections,
  ]);

  return (
    <Stack direction="column" gap="s">
      <Stack
        alignItems="center"
        justifyContent="spaceBetween"
        className={styles.inventoryFilterGroupItem}
        onClick={() =>
          openWithContent(
            <ListingSectionFilterContent
              sections={venueMapInfo?.sections ?? []}
              inventory={inventory}
              selectedSections={listingTileFilters?.sections ?? []}
              onUpdateSelectedSections={onUpdateSectionFilter}
              onClose={close}
            />,
            { size: '95%' }
          )
        }
      >
        <Stack direction="column" gap="s">
          <Content id={ContentId.Section} />
          {!isEmpty(sectionsSubtitle) && (
            <div
              title={sectionsSubtitle}
              className={styles.sortAndFilterSubheader}
            >
              {sectionsSubtitle}
            </div>
          )}
        </Stack>
        <ChevronRightIcon size={vars.iconSize.s} />
      </Stack>
      <Stack
        alignItems="center"
        justifyContent="spaceBetween"
        className={styles.inventoryFilterGroupItem}
        onClick={() =>
          openWithContent(
            <ListingQuantityFilteringContent
              inventory={inventory}
              activeTab={activeTab}
              quantityFilter={listingTileFilters?.listingQuantities}
              onUpdateQuantityFilter={onUpdateQuantityFilter}
              onClose={close}
            />,
            { size: '95%' }
          )
        }
      >
        <Stack direction="column" gap="s">
          <Content id={ContentId.Quantities} />
          {!isEmpty(quantitiesSubtitle) && (
            <div
              title={quantitiesSubtitle}
              className={styles.sortAndFilterSubheader}
            >
              {quantitiesSubtitle}
            </div>
          )}
        </Stack>
        <ChevronRightIcon size={vars.iconSize.s} />
      </Stack>
    </Stack>
  );
};
